import { useCallback, useMemo } from 'react'
import {
  Activity,
  CalendarClock,
  GraduationCap,
  ListChecks,
  Megaphone,
  Scale,
  SlidersHorizontal,
  TrendingUp,
  UserMinus,
  UserPlus,
} from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import {
  ORG_STR as S,
  fetchAssignmentRules,
  fetchShifts,
  fetchWorkload,
  fetchKpi,
  fetchOnboarding,
  fetchOffboarding,
  fetchStaffActivity,
  fetchAnnouncements,
  fetchTraining,
  fetchStaffSettings,
  type AssignmentRuleRow,
  type ShiftRow,
  type WorkloadRow,
  type KpiRow,
  type OnboardingRow,
  type OffboardingRow,
  type StaffActivityRow,
  type AnnouncementRow,
  type TrainingRow,
  type StaffSettingRow,
} from './staffOrgApi'

/**
 * A23.09–A23.18 — Xodimlar va tashkilot ro'yxat ekranlari (StaffScreen'дан
 * ALOHIDA, module-local `staffOrgApi`). A23.15 JONLI (/admin/staff-activity);
 * qolganlari greenfield → fail-soft bo'sh (endpoint chiqqanda mos keladi).
 * Har biri devApi/RateLimitsScreen naqshi — ustun turi ko'rinishni hal qiladi.
 */

/* ── A23.09 Navbatga tayinlash qoidalari ───────────────────────── */
export function AssignmentRulesScreen() {
  const columns = useMemo<AdminColumn<AssignmentRuleRow>[]>(
    () => [
      { key: 'name', header: S.ruleName, type: 'text', minWidth: 160 },
      { key: 'queue', header: S.queue, type: 'text' },
      { key: 'role', header: S.role, type: 'text' },
      { key: 'strategy', header: S.strategy, type: 'text' },
      { key: 'priority', header: S.priority, type: 'number', width: 110, align: 'right' },
      { key: 'enabled', header: S.enabled, type: 'bool', width: 110 },
    ],
    [],
  )
  const fetcher = useCallback(fetchAssignmentRules, [])
  return (
    <>
      <PageHeader title={S.assignmentRules} screenId="A23.09" />
      <DataTable<AssignmentRuleRow>
        screenId="A23.09"
        permission={PERMISSIONS.STAFF_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <ListChecks size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/* ── A23.10 Ish vaqti va smenalar ──────────────────────────────── */
export function ShiftsScreen() {
  const columns = useMemo<AdminColumn<ShiftRow>[]>(
    () => [
      { key: 'staff', header: S.staff, type: 'text', minWidth: 150 },
      { key: 'role', header: S.role, type: 'text' },
      { key: 'day', header: S.day, type: 'text' },
      { key: 'start', header: S.start, type: 'text', width: 110 },
      { key: 'end', header: S.end, type: 'text', width: 110 },
      { key: 'timezone', header: S.timezone, type: 'text' },
    ],
    [],
  )
  const fetcher = useCallback(fetchShifts, [])
  return (
    <>
      <PageHeader title={S.shifts} screenId="A23.10" />
      <DataTable<ShiftRow>
        screenId="A23.10"
        permission={PERMISSIONS.STAFF_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <CalendarClock size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/* ── A23.11 Yuklama taqsimoti ──────────────────────────────────── */
export function WorkloadScreen() {
  const columns = useMemo<AdminColumn<WorkloadRow>[]>(
    () => [
      { key: 'staff', header: S.staff, type: 'text', minWidth: 150 },
      { key: 'role', header: S.role, type: 'text' },
      { key: 'open_tickets', header: S.openTickets, type: 'number', align: 'right' },
      { key: 'capacity', header: S.capacity, type: 'number', align: 'right' },
      { key: 'utilization', header: S.utilization, type: 'number', width: 120, align: 'right' },
    ],
    [],
  )
  const fetcher = useCallback(fetchWorkload, [])
  return (
    <>
      <PageHeader title={S.workload} screenId="A23.11" />
      <DataTable<WorkloadRow>
        screenId="A23.11"
        permission={PERMISSIONS.STAFF_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Scale size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/* ── A23.12 Samaradorlik ko'rsatkichlari ───────────────────────── */
export function PerformanceKpiScreen() {
  const columns = useMemo<AdminColumn<KpiRow>[]>(
    () => [
      { key: 'staff', header: S.staff, type: 'text', minWidth: 150 },
      { key: 'role', header: S.role, type: 'text' },
      { key: 'handled', header: S.handled, type: 'number', align: 'right' },
      { key: 'avg_response_sec', header: S.avgResponse, type: 'number', align: 'right' },
      { key: 'csat', header: S.csat, type: 'number', width: 100, align: 'right' },
      { key: 'sla_pct', header: S.slaPct, type: 'number', width: 100, align: 'right' },
    ],
    [],
  )
  const fetcher = useCallback(fetchKpi, [])
  return (
    <>
      <PageHeader title={S.kpi} screenId="A23.12" />
      <DataTable<KpiRow>
        screenId="A23.12"
        permission={PERMISSIONS.STAFF_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <TrendingUp size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/* ── A23.13 Onboarding checklist ───────────────────────────────── */
export function OnboardingScreen() {
  const columns = useMemo<AdminColumn<OnboardingRow>[]>(
    () => [
      { key: 'staff', header: S.staff, type: 'text', minWidth: 150 },
      { key: 'step', header: S.step, type: 'text', minWidth: 180 },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'due_at', header: S.dueAt, type: 'date', width: 160 },
    ],
    [],
  )
  const fetcher = useCallback(fetchOnboarding, [])
  return (
    <>
      <PageHeader title={S.onboarding} screenId="A23.13" />
      <DataTable<OnboardingRow>
        screenId="A23.13"
        permission={PERMISSIONS.STAFF_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <UserPlus size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/* ── A23.14 Offboarding (huquqlarni olib tashlash) ─────────────── */
export function OffboardingScreen() {
  const columns = useMemo<AdminColumn<OffboardingRow>[]>(
    () => [
      { key: 'staff', header: S.staff, type: 'text', minWidth: 150 },
      { key: 'step', header: S.step, type: 'text', minWidth: 180 },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'revoked_at', header: S.revokedAt, type: 'date', width: 160 },
    ],
    [],
  )
  const fetcher = useCallback(fetchOffboarding, [])
  return (
    <>
      <PageHeader title={S.offboarding} screenId="A23.14" />
      <DataTable<OffboardingRow>
        screenId="A23.14"
        permission={PERMISSIONS.STAFF_OFFBOARD}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <UserMinus size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/* ── A23.15 Xodim faoliyati auditi — JONLI /admin/staff-activity ── */
export function StaffActivityAuditScreen() {
  const columns = useMemo<AdminColumn<StaffActivityRow>[]>(
    () => [
      { key: 'admin', header: S.staff, type: 'text', minWidth: 160 },
      { key: 'actions', header: S.actions, type: 'number', width: 110, align: 'right' },
      { key: 'last_action', header: S.lastAction, type: 'text', minWidth: 200 },
      { key: 'last_at', header: S.lastAt, type: 'date', width: 170 },
      { key: 'last_ip', header: S.lastIp, type: 'text', width: 140, hiddenByDefault: true },
    ],
    [],
  )
  const fetcher = useCallback(fetchStaffActivity, [])
  return (
    <>
      <PageHeader title={S.activity} screenId="A23.15" description={S.activityDesc} verified />
      <DataTable<StaffActivityRow>
        screenId="A23.15"
        permission={PERMISSIONS.STAFF_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.admin}
        exportable
        emptyState={{ icon: <Activity size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/* ── A23.16 Ichki e'lonlar — JONLI /admin/announcements ────────── */
export function StaffAnnouncementsScreen() {
  const columns = useMemo<AdminColumn<AnnouncementRow>[]>(
    () => [
      { key: 'title', header: S.title, type: 'text', minWidth: 200 },
      { key: 'severity', header: S.severity, type: 'text' },
      { key: 'mandatory', header: S.mandatory, type: 'bool', width: 110 },
      { key: 'live', header: S.live, type: 'bool', width: 100 },
      { key: 'starts_at', header: S.startsAt, type: 'date', width: 160 },
    ],
    [],
  )
  const fetcher = useCallback(fetchAnnouncements, [])
  return (
    <>
      <PageHeader title={S.announcements} screenId="A23.16" verified />
      <DataTable<AnnouncementRow>
        screenId="A23.16"
        permission={PERMISSIONS.STAFF_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Megaphone size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/* ── A23.17 O'quv materiallari ─────────────────────────────────── */
export function TrainingScreen() {
  const columns = useMemo<AdminColumn<TrainingRow>[]>(
    () => [
      { key: 'title', header: S.title, type: 'text', minWidth: 200 },
      { key: 'kind', header: S.kind, type: 'text' },
      { key: 'required', header: S.required, type: 'bool', width: 110 },
      { key: 'completions', header: S.completions, type: 'number', align: 'right' },
      { key: 'updated_at', header: S.updatedAt, type: 'date', width: 160 },
    ],
    [],
  )
  const fetcher = useCallback(fetchTraining, [])
  return (
    <>
      <PageHeader title={S.training} screenId="A23.17" />
      <DataTable<TrainingRow>
        screenId="A23.17"
        permission={PERMISSIONS.STAFF_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <GraduationCap size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/* ── A23.18 Xodim sozlamalari ──────────────────────────────────── */
export function StaffSettingsScreen() {
  const columns = useMemo<AdminColumn<StaffSettingRow>[]>(
    () => [
      { key: 'key', header: S.key, type: 'text', minWidth: 200 },
      { key: 'value', header: S.value, type: 'text', minWidth: 200 },
      { key: 'scope', header: S.scope, type: 'text' },
      { key: 'updated_at', header: S.updatedAt, type: 'date', width: 160 },
    ],
    [],
  )
  const fetcher = useCallback(fetchStaffSettings, [])
  return (
    <>
      <PageHeader title={S.settings} screenId="A23.18" />
      <DataTable<StaffSettingRow>
        screenId="A23.18"
        permission={PERMISSIONS.STAFF_EDIT}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <SlidersHorizontal size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
